import { queryOptions, useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api-client'
import { QueryConfig } from '@/lib/react-query'

export type SubscriptionItemPlan = {
	planId: string
	planSku: string
	name: string
	description: string | null
	billingInterval: string
	isAddon: boolean
	recurringAmount: number
	createdAt: string
	updatedAt: string
	features: unknown | null
}

export type SubscriptionItem = {
	subscriptionItemId: string
	planId: string
	plan?: SubscriptionItemPlan
	quantity: number
	isBasePlan: boolean
	metered: boolean
}

export type SubscriptionAccount = {
	accountId: string
	email: string
	name: string
	phone: string
	state: string | null
	city: string | null
	address1: string | null
	address2: string | null
	createdAt: string | null
	activationDate: string | null
}

export type SubscriptionStore = {
	storeId: string
	name: string
	address1: string | null
	address2: string | null
	city: string | null
	state: string | null
	zipCode: string | null
	country: string | null
	phone: string | null
	email: string | null
}

export type Subscription = {
	subscriptionId: string
	accountId: string
	scope: 'account' | 'store' | string
	storeId: string | null
	source: string
	partnerId: string | null
	chargebeeSubscriptionId: string | null
	status: 'active' | 'paused' | 'canceled' | string
	primaryFlag: boolean
	startAt: string | null
	recurringAmount: number
	currentPeriodStart: string | null
	currentPeriodEnd: string | null
	renewalAt: string | null
	trialEndAt: string | null
	cancelAt: string | null
	canceledAt: string | null
	createdAt: string | null
	updatedAt: string | null
	items: SubscriptionItem[]
	account: SubscriptionAccount
	store?: SubscriptionStore
}

export type UUID = string
export type ISO8601 = string

export type DateRangeQuery = {
	from?: ISO8601 | Date
	to?: ISO8601 | Date
}

export type CommonQuery = {
	page?: number
	size?: number
	search?: string
	sort?: string
	includeTotals?: boolean
}

export type SubscriptionQuery = {
	accountId?: UUID
	storeId?: UUID
	status?: string
	billing?: 'all' | 'direct' | 'fastbound'
	createdAt?: DateRangeQuery
	currentPeriodStart?: DateRangeQuery
	currentPeriodEnd?: DateRangeQuery
	renewalAt?: DateRangeQuery
	trialEndAt?: DateRangeQuery
	cancelAt?: DateRangeQuery
	canceledAt?: DateRangeQuery
	commonQuery?: CommonQuery
}

const toIsoString = (value?: ISO8601 | Date | null): string | undefined => {
	if (!value) return undefined
	if (value instanceof Date) return value.toISOString()
	return value
}

export const serializeSubscriptionQuery = (q?: SubscriptionQuery): Record<string, string | number | boolean> => {
	if (!q) return {}
	const params: Record<string, string | number | boolean> = {}

	if (q.accountId) params['AccountId'] = q.accountId
	if (q.storeId) params['StoreId'] = q.storeId
	if (q.status) params['Status'] = q.status
	if (q.billing && q.billing !== 'all') params['Billing'] = q.billing

	const addRange = (key: string, range?: DateRangeQuery) => {
		if (!range) return
		const from = toIsoString(range.from)
		const to = toIsoString(range.to)
		if (from) params[`${key}.From`] = from
		if (to) params[`${key}.To`] = to
	}

	addRange('CreatedAt', q.createdAt)
	addRange('CurrentPeriodEnd', q.currentPeriodEnd)
	addRange('CurrentPeriodStart', q.currentPeriodStart)
	addRange('RenewalAt', q.renewalAt)
	addRange('TrialEndAt', q.trialEndAt)
	addRange('CancelAt', q.cancelAt)
	addRange('CanceledAt', q.canceledAt)

	if (q.commonQuery) {
		const { page, size, search, sort, includeTotals } = q.commonQuery
		if (typeof page === 'number') params['CommonQuery.Page'] = page
		if (typeof size === 'number') params['CommonQuery.Size'] = size
		if (search) params['CommonQuery.Search'] = search
		if (sort) params['CommonQuery.Sort'] = sort
		if (typeof includeTotals === 'boolean') params['CommonQuery.IncludeTotals'] = includeTotals
	}

	return params
}

type PaginatedResponse<T> = {
    items: T[]
    totalItems: number
    totalPages: number
    page: number
    size: number
    totals?: {
        count: number
        recurringAnnualAmount: number
        recurringMonthlyAmount: number
    }
}

export const getSubscriptions = (params?: SubscriptionQuery): Promise<PaginatedResponse<Subscription>> => {
	return api.get('/v2/subscriptions', { params: serializeSubscriptionQuery(params) })
}

export const getSubscriptionsQueryOptions = (params?: SubscriptionQuery) => {
	const serialized = serializeSubscriptionQuery(params)
	return queryOptions({
		queryKey: ['subscriptions', serialized],
		queryFn: () => getSubscriptions(params),
	})
}

type UseSubscriptionsOptions = {
	params?: SubscriptionQuery
	queryConfig?: QueryConfig<typeof getSubscriptions>
}

export const useSubscriptions = ({ params, queryConfig }: UseSubscriptionsOptions = {}) => {
	return useQuery({
		...getSubscriptionsQueryOptions(params),
		...queryConfig,
	})
}


